import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";
import Sortable from "sortablejs";

// Upload affordances for the brand form: click/drag-drop onto the logo and
// gallery zones, previews and per-file progress, drag-to-reorder over the
// gallery strip, and the removal flags the server reads back
// (brand[remove_logo] and brand[remove_asset_ids][]).
//
// Files upload as soon as they are chosen, not on submit. The declarative
// `direct_upload: true` attribute would defer every transfer to the submit
// event (ActiveStorage's UJS hooks submit, uploads serially, then re-submits),
// which leaves the user waiting after they hit the button. So uploads are
// driven with the DirectUpload API directly — the same approach as
// upload_preview_controller — and each input is cleared the moment its files
// are taken, both so the raw file is never posted alongside the signed id and
// so ActiveStorage's UJS finds nothing left to upload at submit time.
//
// Deliberately separate from upload_preview_controller, which carries ~900
// lines of wizard-only logic (aspect-ratio mismatch, duration meter, review
// grid) that a brand has no use for.
export default class extends Controller {
  static targets = [
    "logoInput", "logoZone", "logoLabel", "logoPreview", "savedLogo", "removeLogoFlag",
    "galleryInput", "galleryZone", "gallery", "uploadStatus"
  ];

  static values = {
    directUploadUrl: String,
    uploadingText:   { type: String, default: "Uploading %{done} of %{total}…" },
    uploadDoneText:  { type: String, default: "Upload complete" },
    uploadErrorText: { type: String, default: "%{name} failed to upload" }
  };

  connect() {
    // key -> { file, tile, kind, blobId, failed }
    this.uploads = new Map();
    this.savedLogoRemoved = false;
    this.form = this.element.closest("form");
    this.form?.addEventListener("submit", this.onSubmit);

    // Saved rows and pending uploads live in the same strip, so a drag can
    // interleave them. Same options as the wizard's clip strip so reordering
    // feels identical in both places — see upload_preview_controller.
    if (this.hasGalleryTarget) {
      this.sortable = Sortable.create(this.galleryTarget, {
        animation: 150,
        easing: "cubic-bezier(0.2, 0, 0, 1)",
        ghostClass: "upload-thumb--ghost"
      });
    }
  }

  disconnect() {
    this.sortable?.destroy();
    this.#revokePreviewUrls();
    this.form?.removeEventListener("submit", this.onSubmit);
  }

  // ── Submit ──

  // Everything is already uploaded in the common case, so this only has to
  // swap in the signed ids. It blocks only when the user beat the transfer.
  onSubmit = (event) => {
    if (this.#failedEntries().length) {
      event.preventDefault();
      this.#renderStatus();
      return;
    }

    if (this.#pendingEntries().length) {
      event.preventDefault();
      this.#renderStatus();
      this.#whenUploadsSettle().then(() => this.form.requestSubmit());
      return;
    }

    this.#injectBlobInputs();
  };

  #injectBlobInputs() {
    this.element.querySelectorAll("input[data-blob-input]").forEach((el) => el.remove());

    const logo = [ ...this.uploads.values() ].find((entry) => entry.kind === "logo" && entry.blobId);
    if (logo) this.#blobInput("brand[logo]", logo.blobId);

    // Walked in DOM order rather than upload order, so the strip the user
    // dragged into shape is the order the server stores. Saved rows carry an
    // id and are named without re-uploading anything.
    this.#galleryTiles().forEach((tile) => {
      if (tile.dataset.assetId) {
        this.#blobInput("brand[gallery_order][]", `asset:${tile.dataset.assetId}`);
        return;
      }

      const entry = this.uploads.get(tile.dataset.uploadKey);
      if (!entry?.blobId) return;

      this.#blobInput("brand[gallery][]", entry.blobId);
      this.#blobInput("brand[gallery_order][]", `blob:${entry.blobId}`);
    });
  }

  // A tile flagged for removal keeps its slot in the DOM until the form is
  // submitted, but must not take part in the order posted for the survivors.
  #galleryTiles() {
    if (!this.hasGalleryTarget) return [];

    return Array.from(this.galleryTarget.querySelectorAll(".filmstrip__item"))
      .filter((tile) => !tile.dataset.pendingRemoval);
  }

  #blobInput(name, value) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    input.dataset.blobInput = "true";
    this.element.appendChild(input);
  }

  #whenUploadsSettle() {
    return new Promise((resolve) => {
      const check = () => {
        if (this.#pendingEntries().length === 0) resolve();
        else setTimeout(check, 200);
      };
      check();
    });
  }

  #pendingEntries() {
    return [ ...this.uploads.values() ].filter((entry) => !entry.blobId && !entry.failed);
  }

  #failedEntries() {
    return [ ...this.uploads.values() ].filter((entry) => entry.failed);
  }

  // ── Logo ──

  openLogo() {
    this.logoInputTarget.click();
  }

  dropLogo(e) {
    e.preventDefault();
    this.#clearDragState();

    const file = e.dataTransfer.files[0];
    if (file) this.#addLogo(file);
  }

  previewLogo() {
    const file = this.logoInputTarget.files[0];
    this.logoInputTarget.value = "";
    if (file) this.#addLogo(file);
  }

  #addLogo(file) {
    this.#discardLogo();

    // A replacement logo supersedes the saved one, so hide the saved tile and
    // clear the remove flag — the server applies the same precedence.
    if (this.hasSavedLogoTarget) this.savedLogoTarget.hidden = true;
    if (this.hasRemoveLogoFlagTarget) this.removeLogoFlagTarget.value = "0";

    this.logoLabelTarget.textContent = file.name;
    const tile = this.#tile(file, () => {
      this.#discardLogo();
      this.logoLabelTarget.textContent = this.logoLabelTarget.dataset.defaultLabel;
      this.#restoreSavedLogoState();
    });

    this.logoPreviewTarget.appendChild(tile);
    this.#startUpload(file, tile, "logo");
  }

  #discardLogo() {
    this.#forgetKind("logo");
    this.#revokePreviewUrls(this.logoPreviewTarget);
    this.logoPreviewTarget.innerHTML = "";
  }

  // Dropping a pending logo returns the form to whatever state the saved logo
  // was in beforehand — including "the user had already marked it for
  // deletion", which a plain unhide would silently undo.
  #restoreSavedLogoState() {
    if (this.hasRemoveLogoFlagTarget) {
      this.removeLogoFlagTarget.value = this.savedLogoRemoved ? "1" : "0";
    }
    if (this.hasSavedLogoTarget) {
      this.savedLogoTarget.hidden = Boolean(this.savedLogoRemoved);
    }
  }

  removeSavedLogo(e) {
    e?.stopPropagation();

    this.savedLogoRemoved = true;
    if (this.hasRemoveLogoFlagTarget) this.removeLogoFlagTarget.value = "1";
    if (!this.hasSavedLogoTarget) return;

    const tile = this.savedLogoTarget.querySelector(".filmstrip__item");
    const hideWrapper = () => { this.savedLogoTarget.hidden = true; };
    tile ? this.#hideTile(tile, hideWrapper) : hideWrapper();
  }

  // ── Gallery ──

  dropGallery(e) {
    e.preventDefault();
    this.#clearDragState();
    this.#addGalleryFiles(e.dataTransfer.files);
  }

  previewGallery() {
    const files = Array.from(this.galleryInputTarget.files);
    this.galleryInputTarget.value = "";
    this.#addGalleryFiles(files);
  }

  // Appends rather than replaces, so a second drop adds to the batch instead
  // of silently discarding the first.
  #addGalleryFiles(files) {
    Array.from(files).forEach((file) => {
      const key = this.#key(file);
      if (this.uploads.has(key)) return;

      const tile = this.#tile(file, () => {
        this.uploads.delete(key);
        this.#revokePreviewUrls(tile);
        tile.remove();
        this.#renderStatus();
      });

      this.galleryTarget.appendChild(tile);
      this.#startUpload(file, tile, "gallery");
    });
  }

  // Removing a saved asset posts its id in brand[remove_asset_ids][]. The
  // hidden field is created on demand so an untouched form submits nothing,
  // and the tile is only hidden — the row is not deleted until the form is
  // submitted and the server acts on remove_asset_ids.
  removeSavedAsset(e) {
    // The tile itself opens the preview modal on click.
    e.stopPropagation();

    const tile = e.target.closest("[data-asset-id]");
    if (!tile || tile.dataset.pendingRemoval) return;

    tile.dataset.pendingRemoval = "true";

    const field = document.createElement("input");
    field.type = "hidden";
    field.name = "brand[remove_asset_ids][]";
    field.value = tile.dataset.assetId;
    this.element.appendChild(field);

    this.#hideTile(tile);
  }

  // Collapses the tile with the filmstrip's existing exit transition, then
  // hides it outright so it stops taking up a slot in the strip. The
  // transition is 1ms under prefers-reduced-motion, so transitionend still
  // fires and the fallback timer only covers a dropped event.
  #hideTile(tile, onHidden) {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      tile.hidden = true;
      onHidden?.();
    };

    tile.classList.add("filmstrip__item--exiting");
    tile.addEventListener("transitionend", finish, { once: true });
    setTimeout(finish, 400);
  }

  // ── Upload ──

  #startUpload(file, tile, kind) {
    const key = this.#key(file);
    const entry = { file, tile, kind, blobId: null, failed: false };
    this.uploads.set(key, entry);
    // How #injectBlobInputs gets from a tile back to its signed id once
    // Sortable has moved the tile away from the order it was uploaded in.
    tile.dataset.uploadKey = key;

    const ring = tile.querySelector(".filmstrip__progress");
    ring.hidden = false;
    tile.classList.add("filmstrip__item--uploading");
    this.#renderStatus();

    const upload = new DirectUpload(file, this.directUploadUrlValue, {
      directUploadWillStoreFileWithXHR: (xhr) => {
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            this.#setProgress(tile, Math.round((event.loaded / event.total) * 100));
          }
        });
      }
    });

    upload.create((error, blob) => {
      // The tile may have been removed while its bytes were still going up.
      if (this.uploads.get(key) !== entry) return;

      tile.classList.remove("filmstrip__item--uploading");
      ring.hidden = true;

      if (error) {
        entry.failed = true;
        tile.classList.add("filmstrip__item--error");
      } else {
        entry.blobId = blob.signed_id;
        tile.classList.remove("filmstrip__item--error");
      }

      this.#renderStatus();
    });
  }

  #setProgress(tile, pct) {
    const ring = tile.querySelector(".filmstrip__progress");
    ring.style.setProperty("--pct", pct);
    ring.querySelector(".filmstrip__progress-label").textContent = `${pct}%`;
  }

  #forgetKind(kind) {
    this.uploads.forEach((entry, key) => {
      if (entry.kind === kind) this.uploads.delete(key);
    });
  }

  #renderStatus() {
    if (!this.hasUploadStatusTarget) return;

    const failed = this.#failedEntries();
    if (failed.length) {
      this.uploadStatusTarget.hidden = false;
      this.uploadStatusTarget.textContent =
        this.uploadErrorTextValue.replace("%{name}", failed[0].file.name);
      return;
    }

    const total = this.uploads.size;
    if (total === 0) {
      this.uploadStatusTarget.hidden = true;
      return;
    }

    const done = total - this.#pendingEntries().length;
    this.uploadStatusTarget.hidden = false;
    this.uploadStatusTarget.textContent = done >= total
      ? this.uploadDoneTextValue
      : this.uploadingTextValue
          .replace("%{done}", done + 1)
          .replace("%{total}", total);
  }

  // ── Drag state ──

  dragover(e) {
    e.preventDefault();
    e.currentTarget.classList.add("upload-zone--dragover");
  }

  dragleave() {
    this.#clearDragState();
  }

  #clearDragState() {
    if (this.hasLogoZoneTarget) this.logoZoneTarget.classList.remove("upload-zone--dragover");
    if (this.hasGalleryZoneTarget) this.galleryZoneTarget.classList.remove("upload-zone--dragover");
  }

  // ── Internals ──

  #key(file) {
    return `${file.name}:${file.size}:${file.lastModified}`;
  }

  // Builds one filmstrip tile for a file being uploaded, reusing the markup
  // the server renders for saved assets.
  #tile(file, onRemove) {
    const item = document.createElement("div");
    item.className = "filmstrip__item";

    const url = URL.createObjectURL(file);
    const isVideo = file.type.startsWith("video/");

    // Same click-to-enlarge affordance the saved tiles get, via the shared
    // asset-preview controller. No revokeOnClose — the tile itself still
    // renders from this object URL after the modal closes.
    item.setAttribute("role", "button");
    item.setAttribute("tabindex", "0");
    item.setAttribute("aria-label", `Preview ${file.name}`);
    item.dataset.controller = "asset-preview";
    item.dataset.action = "click->asset-preview#open keydown.enter->asset-preview#open";
    item.dataset.assetPreviewUrlValue = url;
    item.dataset.assetPreviewTypeValue = isVideo ? "video" : "image";

    if (isVideo) {
      const video = document.createElement("video");
      video.src = url;
      video.muted = true;
      video.playsInline = true;
      item.appendChild(video);
    } else {
      const img = document.createElement("img");
      img.src = url;
      img.alt = file.name;
      item.appendChild(img);
    }

    // Matches the IMG/VID chip the saved tiles and the wizard filmstrip show.
    const badge = document.createElement("span");
    badge.className = "filmstrip__badge";
    badge.setAttribute("aria-hidden", "true");
    badge.textContent = isVideo ? "VID" : "IMG";
    item.appendChild(badge);

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "filmstrip__remove";
    remove.setAttribute("aria-label", `Remove ${file.name}`);
    remove.innerHTML =
      '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true">' +
      '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
    // Without this the click bubbles to the tile and opens the preview modal
    // for the file that was just removed.
    remove.addEventListener("click", (e) => {
      e.stopPropagation();
      onRemove();
    });
    item.appendChild(remove);

    // Determinate progress ring, revealed while this file is transferring.
    const ring = document.createElement("div");
    ring.className = "filmstrip__progress filmstrip__progress--determinate";
    ring.hidden = true;
    ring.style.setProperty("--pct", 0);

    const label = document.createElement("span");
    label.className = "filmstrip__progress-label";
    label.textContent = "0%";
    ring.appendChild(label);
    item.appendChild(ring);

    return item;
  }

  // Object URLs stay alive until explicitly revoked, so a form the user edits
  // repeatedly would otherwise hold every preview it ever made.
  #revokePreviewUrls(scope) {
    const roots = scope ? [ scope ] : [
      this.hasLogoPreviewTarget ? this.logoPreviewTarget : null,
      this.hasGalleryTarget ? this.galleryTarget : null
    ].filter(Boolean);

    roots.forEach((root) => {
      const media = root.matches?.("img, video") ? [ root ] : root.querySelectorAll("img[src^='blob:'], video[src^='blob:']");
      media.forEach((el) => URL.revokeObjectURL(el.src));
    });
  }
}
